/**
 * Lingala catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Lingala at a mathematician's register. **Warrant → «Bondimisi»**
 * (justification — flagged). Scientific loanwords are used where Lingala standardly borrows
 * (logic → Loziki, modal → Modale, heuristic, scalar, geometry). W/M grades stay Latin
 * (`::numbering-system/latn`). CLDR cardinal categories are one / other (`one` covers 0–1); the
 * noun uses the n-/ba- pattern (ndelo → bandelo).
 *
 * NOTE (honesty over guessing): Lingala's standardized mathematical/epistemic register is thin;
 * this catalog is a best-effort draft flagged "expert-review suggested" in
 * `Agent9_Batch3_REPORT.md`. Per INV-STABLE-ID any correction changes only the string.
 */
import { P2 } from '../message-ids-phase2.js';
export const LN_CATALOG = {
    locale: 'ln',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Boluki',
        [P2.sectionClaims]: 'Maloba',
        [P2.sectionIntegrity]: 'Bosembo',
        [P2.axisWarrantLabel]: 'Bondimisi',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirike',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Ndimbola} interp {Bolimboli} testable {Ekoki komekama} tested {Emekami} refuted {Eboyami} program {Programe ya boluki} philos {Ya filozofi} other {Eyebani te}}',
        [P2.axisModalLabel]: 'Modale',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Nivo ya makasi {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Ekoki kolukama',
        [P2.stateRefuted]: 'Eboyami',
        [P2.stateOutside]: 'Libanda ya etando',
        [P2.verdictConflict]: 'Ezali kotɛlɛmɛlana na {constraints}',
        [P2.verdictOutside]: 'Esengeli {missing}',
        [P2.lossRegisterPlain]: 'Esalemi pɛtɛɛ na monɔkɔ ya momesano — ndambo ya bosikisiki elongwami.',
        [P2.lossRegisterKid]: 'Esalemi pɛtɛɛ mpo na batángi ya bana — makambo mingi elongwami.',
        [P2.lossProfileDetail]: 'Profil mobimba ya Bondimisi / Empirike / Modale ekomami mokuse awa.',
        [P2.lossConflictDetail]: 'Makambo ya bowelani ekomami mokuse na emonaneli oyo.',
        [P2.laneVerified]: 'Endimami',
        [P2.laneUnverified]: 'Endimami te',
        [P2.laneConflict]: 'Bowelani emonisami',
        [P2.constraintLogic]: 'Loziki',
        [P2.constraintLightBends]: 'Pole egumbamaka na esika ya benga',
        [P2.constraintParallelPostulate]: 'Postula ya milongo oyo ekokani',
        [P2.claimScalarGravity]: 'Teoría ya skalere ya benga',
        [P2.claimNonEuclidean]: 'Zeometri oyo ezali ya Euclide te',
        [P2.claimSubstrateSr]: 'Bolimboli ya substrate ya boyokani ya ntina',
        [P2.edgeExtObservation]: 'Botali ya libanda',
        [P2.edgeResourceSample]: 'Esaleli ya kotanga oyo ekabolami',
        [P2.edgeHeuristicSample]: 'Litambwisi ya heuristik',
        [P2.edgeSharedSample]: 'Moboko oyo ekabolami',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Bandelo',
        [P2.sectionEdges]: 'Mipanzi',
        [P2.sectionFrame]: 'Kadre',
        [P2.sectionExposure]: 'Bomonisi',
        [P2.statusAdoptedFormal]: 'Endimami (ya bonkonzi)',
        [P2.statusAdoptedInterpretive]: 'Endimami (ya bolimboli)',
        [P2.statusSuspended]: 'Etɛlɛmisami',
        [P2.edgeEffectConstraint]: 'Esalaka ndelo (ekoki koboya)',
        [P2.edgeEffectResource]: 'Esaleli — ezali na bopusi te likoló ya bikateli',
        [P2.edgeEffectHeuristic]: 'Heuristik — ezali na bopusi te likoló ya bikateli',
        [P2.edgeEffectShared]: 'Moboko oyo ekabolami — ezali na bopusi te likoló ya bikateli',
        [P2.frameActive]: '{count, plural, =0 {Bandelo ya kosala ezali te} one {# ndelo ezali kosala} other {# bandelo ezali kosala}}',
        [P2.exposureNone]: 'Kotyela motema ya bolimboli ezali te',
        [P2.exposureDep]: 'Etyeli motema na {id}',
        [P2.laneTrackMachine]: 'Masini',
        [P2.laneTrackExpert]: 'Moyebi',
        [P2.laneTrackCommunity]: 'Lisanga',
        [P2.expertAudited]: 'Etalami',
        [P2.expertInProgress]: 'Ezali kokende',
        [P2.expertNotReviewed]: 'Etalami lisusu te',
        [P2.communityFlags]: 'Bilembo ezali',
        [P2.communityNone]: 'Bilembo ezali te',
        [P2.controlChallenge]: 'Boya',
        [P2.controlActivate]: 'Pelisa',
        [P2.controlMakeInterpretive]: 'Sala ya bolimboli',
        [P2.controlImportEdge]: 'Kotisa mopanzi',
        [P2.controlPromote]: 'Matisa',
        [P2.controlUndo]: 'Zongisa',
        [P2.controlRedo]: 'Sala lisusu',
        [P2.controlRegister]: 'Komisa',
        [P2.promoteRejected]: 'Komatisa eboyami',
        [P2.promoteAccepted]: 'Komatisa endimami',
        [P2.promoteReasonRefutationTerminal]: 'Elembeteli ekoki kobikisa boboyi te.',
        [P2.promoteReasonKeystone]: 'Komatisa ya ntina esengeli lisungi ya empirike.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Lolenge ya empirike ekatami te.',
        [P2.promoteReasonModalNotFixed]: 'Nivo ya modale ekatami te.',
        [P2.promoteReasonWarrantNotImproved]: 'Nivo ya bondimisi ebongisami te.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=ln.js.map